import { computed, Directive, ElementRef, HostBinding, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  BLAZE_ELEMENT_CLASS,
  BLAZE_TARGET_CLASS,
  CanvasElementId,
  ELEMENT_ID_ATTR,
  ELEMENT_INDEX_ATTR,
  ELEMENT_PARENT_ID_ATTR,
  ELEMENT_SLOT_ATTR,
  RendererNodeItem,
  SlotId,
} from '@blaze-runtime/engine-domain';
import { wasEventHandled } from '@blaze-runtime/engine-util';
import { RuntimeMode } from '@blaze-shared/app-state-data-access';
import { LEFT_MOUSE_BUTTON } from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';

import { State } from '../../+state';
import { elementMouseDown, elementSelect } from '../../+state/canvas/canvas.actions';
import { addEventListenerForRuntimeMode } from '../../utils/runtime';

// TODO: test
@Directive({
  selector: '[blazeElementNode]',
  standalone: false,
  host: {
    '[class.full-width]': 'isFullWidth()',
    '[class.full-height]': 'isFullHeight()',
    '[class.fit-content-width]': 'fitContentWidth()',
    '[class.fit-content-height]': 'fitContentHeight()',
    '[class.selected]': 'isSelected()',
    '[class.on-path]': 'isOnPath()',
    '[class.placeholder]': 'isPlaceholder()',
    '[style.width.px]': 'width()',
    '[style.height.px]': 'height()',
    '[style.min-height.px]': 'minHeight()',
  },
})
export class ElementNodeDirective {
  private readonly elementRef = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private readonly store = inject<Store<State>>(Store);
  private readonly cssMediaService = inject(CssMediaService);

  blazeElementNode = input.required<RendererNodeItem>();

  private readonly isPrintMode = this.cssMediaService.isPrintMode;

  private onElementMouseDown = (mouseDownEvent: MouseEvent): void => {
    if (
      mouseDownEvent.button !== LEFT_MOUSE_BUTTON ||
      this.blazeElementNode()?.element?.id === undefined ||
      this.blazeElementNode()?.element?.type === 'root'
    ) {
      return;
    }
    const rowContext = this.blazeElementNode().elementContext?.rowContext;

    if (wasEventHandled(mouseDownEvent) || this.allowPassThrough(mouseDownEvent)) {
      mouseDownEvent.stopPropagation();

      // If target element consumes the event, just select the element
      this.store.dispatch(
        elementSelect({ elementId: this.blazeElementNode().element.id, rowContext }),
      );
      return;
    }

    mouseDownEvent.stopPropagation();
    mouseDownEvent.preventDefault();

    this.store.dispatch(
      elementMouseDown({
        mouseDownEvent,
        elementId: this.blazeElementNode().element.id,
        rowContext,
      }),
    );
  };

  isFullWidth = computed(() => {
    return this.forceExpand() || this.elementWidth()?.mode === 'full';
  });

  isFullHeight = computed(() => {
    if (this.isPrintMode()) {
      return undefined;
    }

    return this.elementHeight()?.mode === 'full';
  });

  fitContentWidth = computed(() => {
    return (
      !this.forceExpand() &&
      this.elementWidth()?.mode === 'value' &&
      this.elementWidth()?.fixed === false
    );
  });

  fitContentHeight = computed(() => {
    if (this.isPrintMode()) {
      return undefined;
    }

    return this.elementHeight()?.mode === 'value' && this.elementHeight()?.fixed === false;
  });

  isSelected = computed(() => {
    return this.blazeElementNode().isSelected || false;
  });

  isOnPath = computed(() => {
    return this.blazeElementNode().isOnPath || false;
  });

  isPlaceholder = computed(() => {
    return this.blazeElementNode()?.element?.placeholderMode === 'target';
  });

  width = computed(() => {
    return this.forceExpand() ? undefined : this.widthPx();
  });

  height = computed(() => {
    if (this.isPrintMode()) {
      return undefined;
    }

    return this.getHeight();
  });

  minHeight = computed(() => {
    const minHeight =
      this.elementHeight()?.mode === 'value' &&
      this.elementHeight()?.fixed !== true &&
      !this.blazeElementNode()?.features?.autoHeight
        ? this.heightPx()
        : undefined;
    if (this.isPrintMode()) {
      return minHeight || this.getHeight();
    }

    return minHeight;
  });

  forceExpand = computed(() => {
    return this.blazeElementNode().elementContext?.forceExpandElements ?? false;
  });

  elementWidth = computed(() => {
    return this.blazeElementNode()?.sizeOverride?.width ?? this.blazeElementNode()?.element?.width;
  });

  elementHeight = computed(() => {
    return (
      this.blazeElementNode()?.sizeOverride?.height ?? this.blazeElementNode()?.element?.height
    );
  });

  widthPx = computed(() => {
    return this.elementWidth()?.mode === 'value' ? this.elementWidth()?.value : undefined;
  });

  heightPx = computed(() => {
    return this.elementHeight()?.mode === 'value' ? this.elementHeight()?.value : undefined;
  });

  @HostBinding(`attr.${ELEMENT_ID_ATTR}`) get elementId(): CanvasElementId | undefined {
    return this.blazeElementNode()?.element?.id;
  }
  @HostBinding(`attr.${ELEMENT_INDEX_ATTR}`) get itemIndex(): number | undefined {
    return this.blazeElementNode()?.index;
  }
  @HostBinding(`attr.${ELEMENT_PARENT_ID_ATTR}`) get elementParentId():
    | CanvasElementId
    | undefined {
    return this.blazeElementNode()?.element?.parentNode?.id;
  }
  @HostBinding(`attr.${ELEMENT_SLOT_ATTR}`) get elementParentSlot(): SlotId | undefined {
    return this.blazeElementNode()?.element?.parentNode?.slot;
  }

  constructor() {
    this.elementRef.nativeElement.classList.add(BLAZE_TARGET_CLASS);
    this.elementRef.nativeElement.classList.add(BLAZE_ELEMENT_CLASS);
    addEventListenerForRuntimeMode(
      'mousedown',
      this.onElementMouseDown,
      this.elementRef.nativeElement,
      RuntimeMode.Editor,
    );
  }

  private allowPassThrough(event: MouseEvent): boolean {
    const t = event.target as HTMLElement;
    return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA';
  }

  private getHeight(): number | undefined {
    return (this.elementHeight()?.mode !== 'value' || this.elementHeight()?.fixed === true) &&
      !this.blazeElementNode()?.features?.autoHeight
      ? this.heightPx()
      : undefined;
  }
}
